import React from 'react';
import classNames from 'classnames';
import styles from './pill-switch.module.scss';

type PillSwitchOption<TValue extends string> = {
    value: TValue;
    label: React.ReactNode;
    disabled?: boolean;
};

interface PillSwitchProps<TValue extends string> {
    value: TValue;
    options: [PillSwitchOption<TValue>, PillSwitchOption<TValue>];
    onChange: (value: TValue) => void;
    className?: string;
}

export default function PillSwitch<TValue extends string>({
    value,
    options,
    onChange,
    className,
}: PillSwitchProps<TValue>) {
    const activeIndex = options.findIndex((option) => option.value === value);
    const handleOptionClick = (option: PillSwitchOption<TValue>) => {
        const nextOption = option.value === value
            ? options.find((item) => item.value !== value && !item.disabled)
            : option;

        if (nextOption) {
            onChange(nextOption.value);
        }
    };

    return (
        <div
            className={classNames(styles.switch, className, {
                [styles.switchSecondActive]: activeIndex === 1,
            })}
        >
            {options.map((option) => (
                <button
                    key={option.value}
                    type='button'
                    className={styles.button}
                    aria-pressed={option.value === value}
                    disabled={option.disabled}
                    onClick={() => handleOptionClick(option)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}

export type {PillSwitchOption, PillSwitchProps};
